import React, { useState } from "react";
import { Input, Modal, Popconfirm, Segmented, Select, Switch, Table, Tag, message } from "antd";
import type { TableColumnsType } from "antd";
import { ApolloError } from "@apollo/client";
import dayjs from "dayjs";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import { AuthorityClaimMatchSource, AuthorityClaimStatus } from "constants/field-values";
import { AUTHORITY_CLAIM_MATCH_SOURCE_LABELS, AUTHORITY_CLAIM_STATUS_LABELS } from "constants/constants";
import {
	AuthorityClaimsSubscription,
	useAuthorityClaimsSubscription
} from "graphql/subscriptions/authority-claims.generated";
import { useGetClaimableJurisdictionsQuery } from "graphql/queries/get-claimable-jurisdictions.generated";
import { useInsertAuthorityClaimMutation } from "graphql/mutations/insert-authority-claim.generated";
import { useUpdateAuthorityClaimMutation } from "graphql/mutations/update-authority-claim.generated";
import { useDeleteAuthorityClaimMutation } from "graphql/mutations/delete-authority-claim.generated";
import { useUpdateJurisdictionSetupCompletedMutation } from "graphql/mutations/update-jurisdiction-setup-completed.generated";
import { formatJurisdictionRegion } from "utils/claim-jurisdiction";

type AuthorityClaim = AuthorityClaimsSubscription["authority_claims"][number];

type ClaimFormValues = {
	id: string | null;
	email: string;
	firstName: string;
	lastName: string;
	jurisdictionId: string | null;
	// Name of the jurisdiction the person proposed because it was not in the list. Only set when
	// editing such a claim; it is not editable here.
	proposedJurisdictionName: string | null;
	status: AuthorityClaimStatus;
};

// A claim created here is usually for someone we already checked, so it starts as approved.
const EMPTY_CLAIM_FORM: ClaimFormValues = {
	id: null,
	email: "",
	firstName: "",
	lastName: "",
	jurisdictionId: null,
	proposedJurisdictionName: null,
	status: AuthorityClaimStatus.APPROVED
};

// Value of the status filter that shows every claim.
const ALL_STATUSES = "ALL";

// Same shape check as the service: one "@", no spaces, and a dot in the domain.
const EMAIL_ADDRESS_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STATUS_TAG_COLORS: Record<AuthorityClaimStatus, string> = {
	[AuthorityClaimStatus.PENDING_VERIFICATION]: "default",
	[AuthorityClaimStatus.PENDING_REVIEW]: "warning",
	[AuthorityClaimStatus.APPROVED]: "processing",
	[AuthorityClaimStatus.REJECTED]: "error",
	[AuthorityClaimStatus.COMPLETED]: "success"
};

const STATUS_FILTER_OPTIONS = [
	{ label: AUTHORITY_CLAIM_STATUS_LABELS[AuthorityClaimStatus.PENDING_REVIEW], value: AuthorityClaimStatus.PENDING_REVIEW },
	{
		label: AUTHORITY_CLAIM_STATUS_LABELS[AuthorityClaimStatus.PENDING_VERIFICATION],
		value: AuthorityClaimStatus.PENDING_VERIFICATION
	},
	{ label: AUTHORITY_CLAIM_STATUS_LABELS[AuthorityClaimStatus.APPROVED], value: AuthorityClaimStatus.APPROVED },
	{ label: AUTHORITY_CLAIM_STATUS_LABELS[AuthorityClaimStatus.COMPLETED], value: AuthorityClaimStatus.COMPLETED },
	{ label: AUTHORITY_CLAIM_STATUS_LABELS[AuthorityClaimStatus.REJECTED], value: AuthorityClaimStatus.REJECTED },
	{ label: "All", value: ALL_STATUSES }
];

const STATUS_SELECT_OPTIONS = Object.values(AuthorityClaimStatus).map(status => ({
	label: AUTHORITY_CLAIM_STATUS_LABELS[status],
	value: status
}));

const formatClaimDate = (value?: string | null) => (value ? dayjs(value).format("MMM D, YYYY h:mm A") : "—");

const readServerMessage = (error: unknown, fallbackMessage: string) =>
	(error as ApolloError).graphQLErrors?.[0]?.message || fallbackMessage;

const buildFormValuesFromClaim = (claim: AuthorityClaim): ClaimFormValues => ({
	id: claim.id,
	email: claim.email,
	firstName: claim.first_name,
	lastName: claim.last_name,
	jurisdictionId: claim.jurisdiction_id,
	proposedJurisdictionName: claim.new_jurisdiction_name ?? null,
	status: claim.status as AuthorityClaimStatus
});

// Name and place of the jurisdiction a claim proposed, shaped like a jurisdiction row.
const buildProposedJurisdiction = (claim: AuthorityClaim) => ({
	id: "",
	name: claim.new_jurisdiction_name ?? "",
	city: claim.new_jurisdiction_city,
	county: claim.new_jurisdiction_county,
	state: claim.new_jurisdiction_state
});

/**
 * Admin screen of the claims made from /claim. A claim whose jurisdiction was picked by hand, or
 * that proposes a jurisdiction that is not in the list, waits here until an admin approves or
 * rejects it. A proposed jurisdiction is created when the person opens the approval link. Admins can also create, edit and delete
 * claims. Saving a claim as Approved (new or existing) makes the service email the sign-in
 * link; the account is created when the person opens it.
 */
const AdminAuthorityClaims = () => {
	const [statusFilter, setStatusFilter] = useState<string>(AuthorityClaimStatus.PENDING_REVIEW);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [formValues, setFormValues] = useState<ClaimFormValues>(EMPTY_CLAIM_FORM);
	const [isFormTouched, setIsFormTouched] = useState(false);
	const [formError, setFormError] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [claimIdBeingChanged, setClaimIdBeingChanged] = useState<string | null>(null);
	const [jurisdictionIdBeingChanged, setJurisdictionIdBeingChanged] = useState<string | null>(null);

	const { data: claimsData, loading: isLoadingClaims } = useAuthorityClaimsSubscription();
	const { data: jurisdictionsData } = useGetClaimableJurisdictionsQuery();
	// errorPolicy "none" makes the promise reject on a GraphQL error, so each catch shows the
	// message of the server. The app client default ("all") would return it inside the result.
	const [insertAuthorityClaim] = useInsertAuthorityClaimMutation({ errorPolicy: "none" });
	const [updateAuthorityClaim] = useUpdateAuthorityClaimMutation({ errorPolicy: "none" });
	const [deleteAuthorityClaim] = useDeleteAuthorityClaimMutation({ errorPolicy: "none" });
	const [updateJurisdictionSetupCompleted] = useUpdateJurisdictionSetupCompletedMutation({ errorPolicy: "none" });

	const allClaims = claimsData?.authority_claims ?? [];
	const visibleClaims =
		statusFilter === ALL_STATUSES ? allClaims : allClaims.filter(claim => claim.status === statusFilter);

	const jurisdictionOptions = (jurisdictionsData?.jurisdictions ?? []).map(jurisdiction => ({
		label: `${jurisdiction.name} · ${formatJurisdictionRegion(jurisdiction)}`,
		value: jurisdiction.id
	}));

	const normalizedEmail = formValues.email.trim().toLowerCase();
	const isEmailValid = EMAIL_ADDRESS_PATTERN.test(normalizedEmail);
	// A claim that proposes a new jurisdiction can stay without one: the service creates it.
	const hasJurisdiction = !!formValues.jurisdictionId || !!formValues.proposedJurisdictionName;
	const isFormValid = isEmailValid && !!formValues.firstName.trim() && !!formValues.lastName.trim() && hasJurisdiction;

	const openCreateModal = () => {
		setFormValues(EMPTY_CLAIM_FORM);
		setIsFormTouched(false);
		setFormError("");
		setIsModalOpen(true);
	};

	const openEditModal = (claim: AuthorityClaim) => {
		setFormValues(buildFormValuesFromClaim(claim));
		setIsFormTouched(false);
		setFormError("");
		setIsModalOpen(true);
	};

	const changeClaimStatus = async (claim: AuthorityClaim, status: AuthorityClaimStatus) => {
		setClaimIdBeingChanged(claim.id);
		try {
			const result = await updateAuthorityClaim({ variables: { id: claim.id, set: { status } } });
			// A row outside the permission filter comes back as null without an error.
			if (!result.data?.update_authority_claims_by_pk) {
				message.error("We couldn't update that claim.");
				return;
			}

			message.success(
				status === AuthorityClaimStatus.APPROVED
					? `Approved. We are emailing ${claim.email} a sign-in link.`
					: "Claim rejected. No account will be created."
			);
		} catch (error) {
			console.error("AdminAuthorityClaims changeClaimStatus error:", error);
			message.error(readServerMessage(error, "We couldn't update that claim. Please try again."));
		} finally {
			setClaimIdBeingChanged(null);
		}
	};

	// The value belongs to the jurisdiction, so it changes on every claim of the same jurisdiction.
	// While it is false, the users of those claims see the setup meeting modal on the Ollie dashboard.
	const changeJurisdictionSetupCompleted = async (jurisdictionId: string, setupCompleted: boolean) => {
		setJurisdictionIdBeingChanged(jurisdictionId);
		try {
			const result = await updateJurisdictionSetupCompleted({ variables: { id: jurisdictionId, setupCompleted } });
			// A row outside the permission filter comes back as null without an error.
			if (!result.data?.update_jurisdictions_by_pk) {
				message.error("We couldn't update the jurisdiction setup.");
				return;
			}

			message.success(
				setupCompleted
					? "Setup marked as completed. Users of this jurisdiction will not see the setup meeting modal."
					: "Setup marked as pending. Users of this jurisdiction will see the setup meeting modal again."
			);
		} catch (error) {
			console.error("AdminAuthorityClaims changeJurisdictionSetupCompleted error:", error);
			message.error(readServerMessage(error, "We couldn't update the jurisdiction setup. Please try again."));
		} finally {
			setJurisdictionIdBeingChanged(null);
		}
	};

	const handleDelete = async (claim: AuthorityClaim) => {
		setClaimIdBeingChanged(claim.id);
		try {
			const result = await deleteAuthorityClaim({ variables: { id: claim.id } });
			if (!result.data?.delete_authority_claims_by_pk) {
				message.error("We couldn't delete that claim.");
				return;
			}
			message.success("Claim deleted.");
		} catch (error) {
			console.error("AdminAuthorityClaims handleDelete error:", error);
			message.error(readServerMessage(error, "We couldn't delete that claim. Please try again."));
		} finally {
			setClaimIdBeingChanged(null);
		}
	};

	const handleSave = async () => {
		setIsFormTouched(true);
		if (!isFormValid) return;

		setIsSaving(true);
		setFormError("");

		const claimFields = {
			email: normalizedEmail,
			first_name: formValues.firstName.trim(),
			last_name: formValues.lastName.trim(),
			jurisdiction_id: formValues.jurisdictionId,
			status: formValues.status
		};

		try {
			if (formValues.id) {
				const result = await updateAuthorityClaim({ variables: { id: formValues.id, set: claimFields } });
				if (!result.data?.update_authority_claims_by_pk) {
					setFormError("We couldn't update that claim.");
					return;
				}
			} else {
				// The email link opens the app where the admin created the claim.
				await insertAuthorityClaim({
					variables: {
						object: { ...claimFields, app_base_url: `${window.location.origin}${process.env.PUBLIC_URL}` }
					}
				});
			}

			setIsModalOpen(false);
			message.success(formValues.id ? "Claim updated." : "Claim created.");
		} catch (error) {
			console.error("AdminAuthorityClaims handleSave error:", error);
			setFormError(readServerMessage(error, "We couldn't save the claim. Please try again."));
		} finally {
			setIsSaving(false);
		}
	};

	const columns: TableColumnsType<AuthorityClaim> = [
		{
			title: "Person",
			key: "person",
			render: (_, claim) => (
				<div className="admin-authority-claims-cell">
					<span className="admin-authority-claims-cell-primary">
						{claim.first_name} {claim.last_name}
					</span>
					<span className="admin-authority-claims-cell-secondary">{claim.email}</span>
				</div>
			)
		},
		{
			title: "Jurisdiction",
			key: "jurisdiction",
			render: (_, claim) => {
				if (!claim.jurisdiction && claim.new_jurisdiction_name) {
					return (
						<div className="admin-authority-claims-cell">
							<span className="admin-authority-claims-cell-primary">
								{claim.new_jurisdiction_name} <Tag color="purple">New</Tag>
							</span>
							<span className="admin-authority-claims-cell-secondary">
								{formatJurisdictionRegion(buildProposedJurisdiction(claim))}
							</span>
						</div>
					);
				}

				return (
					<div className="admin-authority-claims-cell">
						<span className="admin-authority-claims-cell-primary">{claim.jurisdiction?.name}</span>
						<span className="admin-authority-claims-cell-secondary">
							{claim.jurisdiction ? formatJurisdictionRegion(claim.jurisdiction) : "—"}
						</span>
					</div>
				);
			}
		},
		{
			title: "Found by",
			key: "matchSource",
			width: 170,
			render: (_, claim) => (
				<span className="admin-authority-claims-cell-secondary">
					{AUTHORITY_CLAIM_MATCH_SOURCE_LABELS[claim.match_source as AuthorityClaimMatchSource] ?? claim.match_source}
				</span>
			)
		},
		{
			title: "Status",
			key: "status",
			width: 150,
			render: (_, claim) => (
				<Tag color={STATUS_TAG_COLORS[claim.status as AuthorityClaimStatus] ?? "default"}>
					{AUTHORITY_CLAIM_STATUS_LABELS[claim.status as AuthorityClaimStatus] ?? claim.status}
				</Tag>
			)
		},
		{
			title: "Jurisdiction setup",
			key: "jurisdictionSetup",
			width: 170,
			render: (_, claim) => {
				if (!claim.jurisdiction) return <span className="admin-authority-claims-cell-secondary">—</span>;

				const jurisdictionId = claim.jurisdiction.id;
				const isSetupCompleted = claim.jurisdiction.setup_completed;

				return (
					<div className="admin-authority-claims-setup">
						<Switch
							size="small"
							checked={isSetupCompleted}
							loading={jurisdictionIdBeingChanged === jurisdictionId}
							onChange={checked => changeJurisdictionSetupCompleted(jurisdictionId, checked)}
						/>
						<span className="admin-authority-claims-cell-secondary">{isSetupCompleted ? "Completed" : "Pending"}</span>
					</div>
				);
			}
		},
		{
			title: "Dates",
			key: "dates",
			width: 230,
			render: (_, claim) => (
				<div className="admin-authority-claims-cell">
					<span className="admin-authority-claims-cell-secondary">Claimed {formatClaimDate(claim.created_at)}</span>
					<span className="admin-authority-claims-cell-secondary">
						Email verified {formatClaimDate(claim.email_verified_at)}
					</span>
				</div>
			)
		},
		{
			title: "",
			key: "actions",
			width: 280,
			render: (_, claim) => {
				const isBeingChanged = claimIdBeingChanged === claim.id;
				const isWaitingForReview = claim.status === AuthorityClaimStatus.PENDING_REVIEW;

				return (
					<div className="admin-authority-claims-actions">
						{isWaitingForReview && (
							<>
								<button
									type="button"
									className="admin-authority-claims-approve"
									disabled={isBeingChanged}
									onClick={() => changeClaimStatus(claim, AuthorityClaimStatus.APPROVED)}
								>
									<Check size={15} />
									<span>Approve</span>
								</button>
								<button
									type="button"
									className="admin-authority-claims-reject"
									disabled={isBeingChanged}
									onClick={() => changeClaimStatus(claim, AuthorityClaimStatus.REJECTED)}
								>
									<X size={15} />
									<span>Reject</span>
								</button>
							</>
						)}
						<button
							type="button"
							className="admin-authority-claims-icon-button"
							title="Edit claim"
							disabled={isBeingChanged}
							onClick={() => openEditModal(claim)}
						>
							<Pencil size={15} />
						</button>
						<Popconfirm
							title="Delete this claim?"
							description="The account it created, if any, is not deleted."
							okText="Delete"
							okButtonProps={{ danger: true }}
							onConfirm={() => handleDelete(claim)}
						>
							<button
								type="button"
								className="admin-authority-claims-icon-button"
								title="Delete claim"
								disabled={isBeingChanged}
							>
								<Trash2 size={15} />
							</button>
						</Popconfirm>
					</div>
				);
			}
		}
	];

	const emptyStateText =
		statusFilter === AuthorityClaimStatus.PENDING_REVIEW
			? "Claims appear here when somebody picks their jurisdiction from the list instead of matching it with their email, or adds a jurisdiction that is not in the list."
			: "No claims with this status yet.";

	const claimsContent =
		!isLoadingClaims && visibleClaims.length === 0 ? (
			<div className="admin-authority-claims-empty">
				<h2 className="admin-authority-claims-empty-title">
					{statusFilter === AuthorityClaimStatus.PENDING_REVIEW ? "Nothing to review" : "Nothing here yet"}
				</h2>
				<p className="admin-authority-claims-empty-text">{emptyStateText}</p>
			</div>
		) : (
			<Table
				className="admin-authority-claims-table"
				rowKey="id"
				columns={columns}
				dataSource={visibleClaims}
				pagination={{ pageSize: 20, hideOnSinglePage: true }}
				scroll={{ x: 1270 }}
			/>
		);

	const isEditingCompletedClaim =
		!!formValues.id &&
		allClaims.find(claim => claim.id === formValues.id)?.status === AuthorityClaimStatus.COMPLETED;

	return (
		<>
			<OllieLayout>
				<div className="admin-authority-claims">
					<div className="admin-authority-claims-header">
						<div>
							<h1 className="admin-authority-claims-title">Jurisdiction Claims</h1>
							<p className="admin-authority-claims-subtitle">
								People who claimed their jurisdiction from the claim page. Approving a claim emails a sign-in link,
								and the person becomes a manager of the jurisdiction when they open it.
							</p>
						</div>
						<button
							type="button"
							className="admin-authority-claims-button admin-authority-claims-button-primary"
							onClick={openCreateModal}
						>
							<Plus size={16} />
							New claim
						</button>
					</div>

					<Segmented
						className="admin-authority-claims-filter"
						value={statusFilter}
						onChange={value => setStatusFilter(value as string)}
						options={STATUS_FILTER_OPTIONS}
					/>

					{claimsContent}
				</div>
			</OllieLayout>

			<Modal
				open={isModalOpen}
				onCancel={() => setIsModalOpen(false)}
				footer={null}
				closable={false}
				width={560}
				className="admin-authority-claims-modal"
			>
				<h2 className="admin-authority-claims-modal-title">{formValues.id ? "Edit claim" : "New claim"}</h2>
				<p className="admin-authority-claims-modal-subtitle">
					Saving the claim as Approved emails the person a sign-in link. Their account is created when they open it.
				</p>

				<div className="admin-authority-claims-form">
					<div className="admin-authority-claims-form-field admin-authority-claims-form-field-full">
						<label className="admin-authority-claims-label">Email</label>
						<Input
							className="admin-authority-claims-control"
							placeholder="name@department.gov"
							value={formValues.email}
							onChange={event => setFormValues(values => ({ ...values, email: event.target.value }))}
							status={isFormTouched && !isEmailValid ? "error" : undefined}
						/>
						{isFormTouched && !isEmailValid && <p className="admin-authority-claims-error">Enter a valid email.</p>}
					</div>

					<div className="admin-authority-claims-form-field">
						<label className="admin-authority-claims-label">First name</label>
						<Input
							className="admin-authority-claims-control"
							value={formValues.firstName}
							onChange={event => setFormValues(values => ({ ...values, firstName: event.target.value }))}
							status={isFormTouched && !formValues.firstName.trim() ? "error" : undefined}
						/>
					</div>

					<div className="admin-authority-claims-form-field">
						<label className="admin-authority-claims-label">Last name</label>
						<Input
							className="admin-authority-claims-control"
							value={formValues.lastName}
							onChange={event => setFormValues(values => ({ ...values, lastName: event.target.value }))}
							status={isFormTouched && !formValues.lastName.trim() ? "error" : undefined}
						/>
					</div>

					<div className="admin-authority-claims-form-field admin-authority-claims-form-field-full">
						<label className="admin-authority-claims-label">Jurisdiction</label>
						<Select
							className="admin-authority-claims-select"
							showSearch
							optionFilterProp="label"
							placeholder="Search for the jurisdiction"
							options={jurisdictionOptions}
							value={formValues.jurisdictionId}
							onChange={jurisdictionId => setFormValues(values => ({ ...values, jurisdictionId: jurisdictionId ?? null }))}
							allowClear={!!formValues.proposedJurisdictionName}
							status={isFormTouched && !hasJurisdiction ? "error" : undefined}
						/>
						{formValues.proposedJurisdictionName && (
							<p className="admin-authority-claims-hint">
								The person proposed a new jurisdiction: {formValues.proposedJurisdictionName}. Leave this empty to create
								it when they open the approval link, or pick an existing jurisdiction instead.
							</p>
						)}
					</div>

					<div className="admin-authority-claims-form-field admin-authority-claims-form-field-full">
						<label className="admin-authority-claims-label">Status</label>
						<Select
							className="admin-authority-claims-select"
							options={STATUS_SELECT_OPTIONS}
							value={formValues.status}
							onChange={status => setFormValues(values => ({ ...values, status }))}
						/>
						<p className="admin-authority-claims-hint">
							{isEditingCompletedClaim
								? "The account already exists. Changing the status here does not change the account or its role."
								: "Approved sends the sign-in link. Rejected closes the claim without an account."}
						</p>
					</div>
				</div>

				{formError && <p className="admin-authority-claims-error">{formError}</p>}

				<div className="admin-authority-claims-modal-actions">
					<button
						type="button"
						className="admin-authority-claims-button"
						onClick={() => setIsModalOpen(false)}
						disabled={isSaving}
					>
						Cancel
					</button>
					<button
						type="button"
						className="admin-authority-claims-button admin-authority-claims-button-primary"
						onClick={handleSave}
						disabled={isSaving}
					>
						{isSaving ? "Saving…" : "Save claim"}
					</button>
				</div>
			</Modal>

			<LoadingOverlay isLoading={isLoadingClaims && allClaims.length === 0} />
		</>
	);
};

export default AdminAuthorityClaims;
